import { TextField } from '@mui/material';
import React, { SetStateAction, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FaCheck, FaEdit, FaTimes } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import { BiCopy } from 'react-icons/bi';
import { FaCalendarAlt } from 'react-icons/fa';
import {
    formatDate,
    gePlatformIcon,
    removeTimeZoneFromDate,
    truncateString,
} from '../../../../../../../shared/functions/Functions';
import { ErrorToast, SuccessToast } from '../../../../../../../utils/toasters';
import { ManageService } from '../../../service';
import { iManageList } from '../../dto/IManageCalendar';
import CustomDatePicker from '../../../../../../../shared/components/DatePicker';
import { ConfirmationModal } from '../../../../../../../shared/components/modal/ConfirmationModal';
import { useDispatch, useSelector } from 'react-redux';
import { manageBrand } from '../../../../../../../store/brand/manageBrandSlice';
import { IAllStates } from '../../../../../../../store/rootReducer';
import CardLayout from '../../../../../../../shared/components/card/CardLayout';
import TooltipOverlay from '../../../../../../../shared/components/tooltip/Tooltip';
import { CustomImageHandler } from '../../../../../../../shared/components/CustomImageHandler';
import { EditableCopyField } from '../../../../../../../shared/components/EditableCopyField';

interface IProps {
    state: iManageList;
    setState: React.Dispatch<SetStateAction<iManageList>>;
    handleCopy: (val: string) => void;
}

export const statusesToEditTrackinglink = ['proposed', 'scheduled', 'approved', 'feedback_loop', 'reported'];

const CalendarManageLeftCard = ({ state, setState, handleCopy }: IProps) => {
    const dispatch = useDispatch();
    const { isClient } = useSelector((state: IAllStates) => state.auth.user);
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const isGlobalBrandActive = useSelector((states: IAllStates) => states?.auth?.isGlobalBrand);
    const { triggerRescheduleList } = useSelector((state: IAllStates) => state.manageBrand);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isRescheduleClicked, setIsRescheduleClicked] = useState(false);
    const [isApproveEvent, setIsApproveEvent] = useState(false);
    const [isTrackingLinkEdit, setIsTrackingLinkEdit] = useState(false);

    const isGlobalManage = pathname?.includes('brand/global/manage');
    const isProposedInGlobalManage = isGlobalManage && state?.status === 'proposed';
    const canEditTrackingLink = statusesToEditTrackinglink.includes(state?.status);

    const updateTracklink = (eventId: number) => {
        if (state?.trackingLink.length > 250) {
            return ErrorToast({ response: { data: { message: t('brand.campaign.manage.modal.trackLinkError') } } });
        }
        ManageService.updateTrackingLink(eventId, state?.trackingLink)
            .then(response => {
                if (response) {
                    SuccessToast(t('general.toasts.successUpdated'));
                    setIsTrackingLinkEdit(false);
                }
            })
            .catch(error => ErrorToast(error));
    };

    const updateAdCode = async () => {
        try {
            const response = await ManageService.updateAdCode(state?.id, state?.adCode ?? '');
            if (response) {
                SuccessToast(t('general.toasts.successUpdated'));
                return true;
            }
        } catch (error) {
            ErrorToast(error);
        }
        return false;
    };

    const updatePostingLink = async () => {
        try {
            const response = await ManageService.updatePostingLink(state?.id, state?.postingLink ?? '');
            if (response) {
                SuccessToast(t('general.toasts.successUpdated'));
                return true;
            }
        } catch (error) {
            ErrorToast(error);
        }
        return false;
    };

    const reschedulePostRequest = () => {
        ManageService.reschedulePostRequest(state?.id, state?.postingDate)
            .then(response => {
                if (response) {
                    SuccessToast(t('brand.campaign.manage.approved_request_date'));
                    setIsRescheduleClicked(false);
                    setState(prev => ({
                        ...prev,
                        reschedule: response?.reschedule,
                        postingDate: response?.postingDate,
                    }));
                }
            })
            .catch(error => ErrorToast(error));
    };

    const approveDeclineEventRequest = async () => {
        const service = isApproveEvent
            ? ManageService.approveRescheduleRequest(state?.id)
            : ManageService.declineRescheduleRequest(state?.id);

        await service
            .then(response => {
                if (response) {
                    dispatch(manageBrand.actions.setTriggerRescheduleList(!triggerRescheduleList));
                    SuccessToast(
                        t(
                            isApproveEvent
                                ? 'general.toasts.successRescheduled'
                                : 'brand.campaign.manage.declined_request_date'
                        )
                    );
                    setState(prev => ({
                        ...prev,
                        reschedule: response?.reschedule,
                        postingDate: response?.postingDate,
                    }));
                }
            })
            .catch(err => ErrorToast(err));
    };

    const handleChangeTimepicker = (newValue: string | undefined | null, name: string) => {
        setState(prev => ({ ...prev, [name]: newValue }));
    };

    const name =
        state?.approvedBy?.displayName || `${state?.approvedBy?.firstName ?? ''} ${state?.approvedBy?.lastName ?? ''}`;
    const truncatedName = truncateString(name, 20);
    const isTruncated = name.length > 20;
    return (
        <div>
            <ConfirmationModal
                title={t(
                    `influencer.planner.${
                        isApproveEvent ? 'confirm_approve_event_title' : 'confirm_decline_event_title'
                    }`
                )}
                description={t(
                    `influencer.planner.${isApproveEvent ? 'confirm_approve_event' : 'confirm_decline_event'}`
                )}
                show={showConfirmModal}
                closeModal={() => {
                    setShowConfirmModal(false);
                }}
                action={approveDeclineEventRequest}
            />
            {isGlobalBrandActive && (
                <CardLayout classCard="border-primary mb-2" classBody="p-3 d-flex align-items-center">
                    <CustomImageHandler
                        photoPath={state?.offerItem?.offer?.campaign?.campaignImages?.[0]?.thumbnail}
                        classes="middle-rounded-logo"
                        altTag={'Campaign Profile'}
                        name={state?.offerItem?.offer?.campaign?.title}
                    />
                    <h6 className="mb-0 ml-2">{state?.offerItem?.offer?.campaign?.title ?? '-'}</h6>
                </CardLayout>
            )}

            <h6 className="text-muted">
                {t('general.placementId')}: #{state?.id}
            </h6>
            <CardLayout classCard="my-2" classBody="p-3">
                {gePlatformIcon({
                    iconPath: state?.offerItem?.socialPlatform?.path,
                    platformName:
                        t(`general.platforms.${state?.offerItem?.socialPlatform?.name}`) +
                        ` ${state?.platformPosition ?? '-'}/${state?.offerItem?.nbPosts}`,
                })}
                <div className="mt-2">{t('brand.campaign.manage.postingType')}</div>
            </CardLayout>
            <CardLayout classCard="my-2" classBody="p-3">
                <div className="d-flex align-items-center text-primary justify-content-between">
                    {isRescheduleClicked ? (
                        <div className="d-flex align-items-end ">
                            <CustomDatePicker
                                autoOpen
                                disableFrom={removeTimeZoneFromDate(
                                    state?.offerItem?.offer?.campaign?.publishFrom ?? ''
                                )}
                                disableTo={removeTimeZoneFromDate(state?.offerItem?.offer?.campaign?.publishTo ?? '')}
                                value={state?.postingDate}
                                onChange={e => handleChangeTimepicker(e as unknown as string, 'postingDate')}
                                label={t('general.button.reschedule')}
                            />
                            <Button variant="primary" onClick={reschedulePostRequest} className="w-fit ml-1">
                                {t('general.button.save')}
                            </Button>
                        </div>
                    ) : (
                        <h6
                            className={`mb-0 ${!isProposedInGlobalManage ? 'cursor-pointer' : ''}`}
                            onClick={() => {
                                if (!isProposedInGlobalManage) {
                                    setIsRescheduleClicked(prevState => !prevState);
                                }
                            }}
                        >
                            {state?.postingDate ? formatDate(state?.postingDate) : '-'}
                            <FaCalendarAlt className="text-primary font-16 ml-2" />
                        </h6>
                    )}
                    {statusesToEditTrackinglink.includes(state?.status) &&
                        !state?.reschedule &&
                        !isRescheduleClicked &&
                        !isProposedInGlobalManage && (
                            <>
                                {state?.status !== 'reported' && (
                                    <FaEdit
                                        onClick={() => setIsRescheduleClicked(prevState => !prevState)}
                                        className="font-16 cursor-pointer"
                                    />
                                )}
                            </>
                        )}
                </div>
                <div>{t('brand.campaign.manage.postingDate')}</div>
                {!!state?.reschedule?.id && (
                    <div className="d-flex justify-content-between align-items-end">
                        <div className="mt-1">
                            <span className="text-primary position-relative">
                                {formatDate(state?.reschedule?.postingDate)}
                                {statusesToEditTrackinglink.includes(state?.status) &&
                                    state?.reschedule?.fromBrand === false && (
                                        <span style={{ right: -16, top: -4 }} className="notification-bullet-sm" />
                                    )}
                            </span>
                            <div>{t('brand.campaign.manage.requestedDate')}</div>
                        </div>
                        {statusesToEditTrackinglink.includes(state?.status) &&
                            state?.reschedule?.fromBrand === false && (
                                <div>
                                    <FaTimes
                                        className="cursor-pointer mr-2 text-danger font-16"
                                        onClick={() => {
                                            setIsApproveEvent(false);
                                            setShowConfirmModal(true);
                                        }}
                                    />
                                    <FaCheck
                                        onClick={() => {
                                            setIsApproveEvent(true);
                                            setShowConfirmModal(true);
                                        }}
                                        className="font-16 cursor-pointer text-primary"
                                    />
                                </div>
                            )}
                    </div>
                )}
            </CardLayout>
            {!!state?.feedBackLoopDate && (
                <CardLayout classCard="my-2" classBody="p-3">
                    <h6 className="d-flex align-items-center text-primary">
                        {state?.feedBackLoopDate ? formatDate(state?.feedBackLoopDate) : '-'}
                        <FaCalendarAlt className="text-primary font-16 ml-2" />
                    </h6>
                    <div className="d-flex align-items-center">
                        {t('brand.campaign.manage.feedBackLoopEnddate')}
                        <TooltipOverlay
                            className="ms-1"
                            content={t(`brand.campaign.manage.feedBackLoopEnddateTooltip`)}
                        >
                            <FiInfo size={14} />
                        </TooltipOverlay>
                    </div>
                </CardLayout>
            )}
            <CardLayout classCard="my-2" classBody="p-3">
                <h6>{t('influencer.planner.campaignHashtags')}</h6>
                <span className="text-muted"></span>
                <div className="mt-2 word-break-word">
                    {!!state?.offerItem?.offer?.campaign?.hashtags?.length ? (
                        state?.offerItem?.offer?.campaign?.hashtags?.split(' ')?.map((item, i) => (
                            <span key={i} className={`${i > 0 ? 'pl-2' : ''}`}>
                                {item}
                            </span>
                        ))
                    ) : (
                        <span className="text-muted">-</span>
                    )}
                    {!!state?.offerItem?.offer?.campaign?.hashtags && (
                        <BiCopy
                            className="copy-icon ml-1"
                            onClick={() => handleCopy(state?.offerItem?.offer?.campaign?.hashtags ?? '')}
                        />
                    )}
                </div>
                {!!state?.offerItem?.offer?.campaign?.profileTags?.length && (
                    <div className={'mt-3'}>
                        <h6>{t('influencer.offer_management.modal.tagging')}</h6>
                        <div className="mt-2 word-break-word">
                            {state?.offerItem?.offer?.campaign?.profileTags?.split(' ')?.map((item, i) => (
                                <span key={i} className={`${i > 0 ? 'pl-2' : ''}`}>
                                    {item}
                                </span>
                            ))}
                            <BiCopy
                                className="copy-icon ml-1"
                                onClick={() => handleCopy(state?.offerItem?.offer?.campaign?.profileTags ?? '')}
                            />
                        </div>
                    </div>
                )}
            </CardLayout>

            {!isProposedInGlobalManage && (
                <div className="d-flex align-items-center my-3">
                    <TextField
                        label={t('brand.campaign.manage.modal.trackLink')}
                        variant="outlined"
                        value={state?.trackingLink ?? ''}
                        size="small"
                        onChange={e => setState(prev => ({ ...prev, trackingLink: e.target.value }))}
                        disabled={!isTrackingLinkEdit}
                        className="w-100"
                    />
                    <TooltipOverlay className="ms-1" content={t(`brand.campaign.manage.trackLinkTooltip`)}>
                        <FiInfo size={14} />
                    </TooltipOverlay>
                    {state?.id &&
                        (isTrackingLinkEdit ? (
                            <Button
                                onClick={() => updateTracklink(state?.id)}
                                disabled={!state?.trackingLink?.length}
                                variant="primary"
                                className="ml-2"
                            >
                                {t('general.button.save')}
                            </Button>
                        ) : (
                            <div className="d-flex align-items-center ml-2">
                                {canEditTrackingLink && (
                                    <FaEdit
                                        onClick={() => setIsTrackingLinkEdit(true)}
                                        className="font-18 cursor-pointer text-primary"
                                    />
                                )}
                                {!!state?.trackingLink && (
                                    <BiCopy
                                        className={`copy-icon ${canEditTrackingLink ? 'ml-2' : ''}`}
                                        onClick={() => handleCopy(state?.trackingLink ?? '')}
                                    />
                                )}
                            </div>
                        ))}
                </div>
            )}
            <EditableCopyField
                label={t('brand.campaign.manage.modal.adCode')}
                tooltip={t('brand.campaign.manage.modal.adCodeToolTip')}
                value={state?.adCode}
                onChange={value => setState(prev => ({ ...prev, adCode: value }))}
                onSave={updateAdCode}
                showActions={!!state?.id}
            />
            <EditableCopyField
                label={t('influencer.planner.postLink')}
                value={state?.postingLink}
                onChange={value => setState(prev => ({ ...prev, postingLink: value }))}
                onSave={updatePostingLink}
                showActions={!!state?.id}
                canEdit={!isClient}
                className="mb-3"
            />
            <Col className="my-2">
                {state?.approvedBy && (
                    <Row className="booking-card m-0 position-relative p-3">
                        <div className="d-flex align-items-center justify-content-between mb-2 px-0">
                            <p className="mb-0 fw-medium">{t('brand.campaign.dashboard.manage.approved_by')}:</p>

                            <span className="text-muted" style={{ fontSize: '13px' }}>
                                {state?.approvedDate ? formatDate(state?.approvedDate) : ''}
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2 px-0">
                            <CustomImageHandler
                                altTag="User Profile"
                                thumbnailPath={state?.approvedBy?.profilePhoto?.thumbnail}
                                photoPath={state?.approvedBy?.profilePhoto?.path}
                                name={name}
                                style={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                }}
                            />

                            <TooltipOverlay content={isTruncated ? name : ''}>
                                <span style={{ fontSize: '14px', fontWeight: 500 }}>{truncatedName || '-'}</span>
                            </TooltipOverlay>
                        </div>
                    </Row>
                )}
            </Col>
        </div>
    );
};

export default CalendarManageLeftCard;
